/**
 * ActivityResponder — UI for filling out an activity (quiz or survey).
 *
 * Renders all questions in a vertical list, manages local answer state, and
 * submits via either the authenticated /submit endpoint or the public-link
 * /submit endpoint depending on the `mode` prop. The submit handler is passed
 * in so this component can be used by both AuthedResponder and the public page.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, CheckCircle2, XCircle, ClipboardList } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import type {
  Activity,
  ActivityQuestion,
  ActivityResponseInput,
  ActivitySubmission,
  ActivitySubmissionCreate,
} from '@/types/activities'

type AnswerState = Record<string, ActivityResponseInput>

interface ActivityResponderProps {
  activity: Activity
  /** True when activity.collect_respondent_info=true and user is anonymous (public). */
  collectRespondentInfo?: boolean
  /** Submit handler — provided by either the auth wrapper or the public page. */
  onSubmit: (
    payload: ActivitySubmissionCreate,
  ) => Promise<ActivitySubmission>
  /** Hide name/email fields (authenticated path identifies the user already). */
  anonymous?: boolean
  /** Optional — when set, the result screen shows a "Fertig"-button that
   *  calls this callback. Used by the consumer flow to drop the user
   *  back to the library after they've reviewed their score. */
  onDone?: () => void
}

export function ActivityResponder({
  activity,
  collectRespondentInfo = false,
  onSubmit,
  anonymous = false,
  onDone,
}: ActivityResponderProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [answers, setAnswers] = useState<AnswerState>({})
  const [respondentName, setRespondentName] = useState('')
  const [respondentEmail, setRespondentEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ActivitySubmission | null>(null)

  const answerableQuestions = useMemo(
    () => activity.questions.filter((q) => q.question_type !== 'section_header'),
    [activity.questions],
  )

  const updateAnswer = (questionId: string, partial: Partial<ActivityResponseInput>) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...partial,
        question_id: questionId,
      },
    }))
  }

  const handleSubmit = async () => {
    if (submitting) return

    // Client-side validation for required fields
    for (const q of answerableQuestions) {
      if (!q.required) continue
      const a = answers[q.id]
      const empty =
        !a ||
        (q.question_type === 'free_text' && !a.answer_text?.trim()) ||
        ((q.question_type === 'single_choice' || q.question_type === 'multiple_choice') &&
          (!a.answer_options || a.answer_options.length === 0) &&
          !a.answer_other_text?.trim()) ||
        (q.question_type === 'rating' && a.answer_rating == null)
      if (empty) {
        toast({
          variant: 'destructive',
          title: t('activities.requiredFieldsMissing'),
          description: q.question_text,
        })
        return
      }
    }

    if (collectRespondentInfo && (!respondentName.trim() || !respondentEmail.trim())) {
      toast({
        variant: 'destructive',
        title: t('activities.respondentInfoRequired'),
      })
      return
    }

    setSubmitting(true)
    try {
      const payload: ActivitySubmissionCreate = {
        answers: Object.values(answers),
        respondent_name: collectRespondentInfo ? respondentName.trim() : undefined,
        respondent_email: collectRespondentInfo ? respondentEmail.trim() : undefined,
      }
      const submission = await onSubmit(payload)
      setResult(submission)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: t('activities.submitFailed'),
        description: e instanceof Error ? e.message : String(e),
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Number questions skipping section headers (so users see "1, 2, 3"
  // continuous, not "1, 2, [section], 3, 4"). Must run BEFORE the early
  // return for `result` — otherwise the post-submit re-render hits one
  // fewer hook than the previous render and React errors out with
  // "Rendered fewer hooks than expected."
  const questionNumbers = useMemo(() => {
    const map = new Map<string, number>()
    let n = 0
    for (const q of activity.questions) {
      if (q.question_type === 'section_header') continue
      n += 1
      map.set(q.id, n)
    }
    return map
  }, [activity.questions])

  // After-submit confirmation screen
  if (result) {
    return <ResponderResult activity={activity} submission={result} onDone={onDone} />
  }

  const isAssessment = activity.analysis_mode === 'assessment'

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <header className="settings-section flex items-start gap-3 p-5">
        <div className="rounded-lg bg-[var(--theme-interactive-color)]/10 p-2.5 shrink-0">
          <ClipboardList className="h-5 w-5 text-[var(--theme-interactive-color)]" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <h2 className="text-xl font-semibold text-[var(--text-color)]">{activity.title}</h2>
          {activity.description && (
            <p className="text-sm text-[var(--text-secondary)] whitespace-pre-line">{activity.description}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-xs text-[var(--text-secondary)]">
            <span>
              {t('activities.questionCount', { count: answerableQuestions.length })}
            </span>
            {isAssessment && activity.passing_score != null && (
              <span>{t('activities.passingScoreHint', { score: activity.passing_score })}</span>
            )}
          </div>
        </div>
      </header>

      {anonymous && collectRespondentInfo && (
        <div className="settings-section p-5 space-y-3">
          <h3 className="text-sm font-semibold text-[var(--text-color)]">
            {t('activities.yourDetails')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="resp_name">{t('activities.name')} *</Label>
              <Input
                id="resp_name"
                value={respondentName}
                onChange={(e) => setRespondentName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resp_email">{t('activities.email')} *</Label>
              <Input
                id="resp_email"
                type="email"
                value={respondentEmail}
                onChange={(e) => setRespondentEmail(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* All questions in a single surface, separated by hairline dividers.
          Far cleaner than one card per question — keeps the focus on the
          content rather than the chrome around it. */}
      <div className="settings-section divide-y divide-[var(--window-border-color)]">
        {activity.questions.map((q) => (
          <QuestionRenderer
            key={q.id}
            question={q}
            answer={answers[q.id]}
            onChange={(partial) => updateAnswer(q.id, partial)}
            number={questionNumbers.get(q.id)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between gap-4 pt-2">
        <p className="text-xs text-[var(--text-secondary)]">
          {t('activities.requiredHint')}
        </p>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          size="lg"
          className="min-w-36"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('activities.submitting')}
            </>
          ) : (
            t('activities.submit')
          )}
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// Per-question renderer
// ============================================================================

function QuestionRenderer({
  question,
  answer,
  onChange,
  number,
}: {
  question: ActivityQuestion
  answer: ActivityResponseInput | undefined
  onChange: (partial: Partial<ActivityResponseInput>) => void
  /** 1-based question number (skips section headers). Undefined for headers. */
  number?: number
}) {
  const { t } = useTranslation()

  if (question.question_type === 'section_header') {
    return (
      <div className="px-5 py-4 bg-[var(--menu-item-hover)]/40">
        {question.section_title && (
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-color)]">
            {question.section_title}
          </h3>
        )}
        {question.section_description && (
          <p className="text-sm text-[var(--text-secondary)] mt-1 whitespace-pre-line">
            {question.section_description}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="px-5 py-4 space-y-3">
      <div className="flex items-baseline gap-3">
        {number != null && (
          <span className="text-xs font-semibold tabular-nums text-[var(--text-secondary)] shrink-0 mt-0.5">
            {number}.
          </span>
        )}
        <Label className="text-sm font-medium text-[var(--text-color)] leading-relaxed flex-1 whitespace-pre-line">
          {question.question_text}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
      <div className={cn(number != null && 'pl-6')}>
        {question.question_type === 'free_text' && (
          <Textarea
            value={answer?.answer_text ?? ''}
            onChange={(e) => onChange({ answer_text: e.target.value })}
            rows={3}
            placeholder={t('activities.yourAnswer')}
          />
        )}

        {question.question_type === 'single_choice' && (
          <RadioGroup
            value={answer?.answer_options?.[0] ?? ''}
            onValueChange={(v: string) => onChange({ answer_options: [v] })}
            className="space-y-1"
          >
            {question.answer_options.map((opt) => {
              const checked = answer?.answer_options?.[0] === opt
              return (
                <Label
                  key={opt}
                  htmlFor={`q${question.id}_${opt}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors',
                    checked
                      ? 'bg-[var(--theme-interactive-color)]/10 text-[var(--text-color)]'
                      : 'hover:bg-[var(--menu-item-hover)] text-[var(--text-color)]',
                  )}
                >
                  <RadioGroupItem id={`q${question.id}_${opt}`} value={opt} />
                  <span className="flex-1 text-sm">{opt}</span>
                </Label>
              )
            })}
          </RadioGroup>
        )}

        {question.question_type === 'multiple_choice' && (
          <MultipleChoiceField
            question={question}
            answer={answer}
            onChange={onChange}
          />
        )}

        {question.question_type === 'rating' && question.scale_min != null && question.scale_max != null && (
          <RatingField
            min={question.scale_min}
            max={question.scale_max}
            value={answer?.answer_rating ?? null}
            onChange={(v) => onChange({ answer_rating: v })}
          />
        )}
      </div>
    </div>
  )
}

function MultipleChoiceField({
  question,
  answer,
  onChange,
}: {
  question: ActivityQuestion
  answer: ActivityResponseInput | undefined
  onChange: (partial: Partial<ActivityResponseInput>) => void
}) {
  const { t } = useTranslation()
  const otherInputRef = useRef<HTMLInputElement>(null)
  const selected = answer?.answer_options ?? []
  const otherText = answer?.answer_other_text ?? ''
  // Non-empty other-text counts as the implicit "Andere" selection. Tracked
  // separately on the API side (answer_other_text), so the inline row is a
  // visual surface for the same state — no sentinel pushed into answer_options.
  const otherSelected = otherText.trim().length > 0
  const max = question.max_selections
  const usedSlots = selected.length + (otherSelected ? 1 : 0)

  const toggle = (opt: string) => {
    if (selected.includes(opt)) {
      onChange({ answer_options: selected.filter((s) => s !== opt) })
      return
    }
    if (max != null && usedSlots >= max) return // hard-cap
    onChange({ answer_options: [...selected, opt] })
  }

  const handleOtherToggle = () => {
    if (otherSelected) {
      onChange({ answer_other_text: '' })
      return
    }
    // Not yet selected: focus the input so the user can type to "tick" it.
    otherInputRef.current?.focus()
  }

  const otherDisabled = !otherSelected && max != null && usedSlots >= max

  return (
    <div className="space-y-1">
      {max != null && (
        <p className="text-xs text-[var(--text-secondary)] pb-1">
          {t('activities.maxSelectionsHint', { max })}
        </p>
      )}
      {question.answer_options.map((opt) => {
        const checked = selected.includes(opt)
        const disabled = !checked && max != null && usedSlots >= max
        return (
          <Label
            key={opt}
            htmlFor={`q${question.id}_${opt}`}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md transition-colors',
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer',
              checked
                ? 'bg-[var(--theme-interactive-color)]/10'
                : !disabled && 'hover:bg-[var(--menu-item-hover)]',
            )}
          >
            <Checkbox
              id={`q${question.id}_${opt}`}
              checked={checked}
              onCheckedChange={() => toggle(opt)}
              disabled={disabled}
            />
            <span className="flex-1 text-sm text-[var(--text-color)]">{opt}</span>
          </Label>
        )
      })}
      {question.allow_other_text && (
        <Label
          htmlFor={`q${question.id}_other_cb`}
          className={cn(
            'flex items-center gap-3 px-3 py-2 rounded-md transition-colors',
            otherDisabled
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer',
            otherSelected
              ? 'bg-[var(--theme-interactive-color)]/10'
              : !otherDisabled && 'hover:bg-[var(--menu-item-hover)]',
          )}
        >
          <Checkbox
            id={`q${question.id}_other_cb`}
            checked={otherSelected}
            onCheckedChange={handleOtherToggle}
            disabled={otherDisabled}
          />
          <span className="text-sm text-[var(--text-color)] shrink-0">
            {t('activities.otherTextLabel')}
          </span>
          <Input
            ref={otherInputRef}
            id={`q${question.id}_other`}
            value={otherText}
            onChange={(e) => onChange({ answer_other_text: e.target.value })}
            // Block the wrapping Label's htmlFor → Checkbox synthetic click on
            // both mouse and keyboard entry into the input.
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            placeholder={t('activities.otherTextPlaceholder')}
            disabled={otherDisabled}
            className="flex-1 h-8"
          />
        </Label>
      )}
    </div>
  )
}

function RatingField({
  min,
  max,
  value,
  onChange,
}: {
  min: number
  max: number
  value: number | null
  onChange: (v: number) => void
}) {
  const { t } = useTranslation()
  const buttons = useMemo(() => {
    const out: number[] = []
    for (let i = min; i <= max; i++) out.push(i)
    return out
  }, [min, max])

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  /** Marks that the most recent value change came from a keyboard event,
   *  so we know to imperatively move focus. Click changes don't need it
   *  (the click already focuses the button). */
  const movedByKeyRef = useRef(false)

  // After value changes due to a keyboard event, move DOM focus to the newly
  // selected button. Required by the ARIA roving-tabindex pattern: focus must
  // follow selection so keyboard-only users see the focus ring track the value.
  useEffect(() => {
    if (!movedByKeyRef.current || value == null) return
    const idx = buttons.indexOf(value)
    if (idx >= 0) buttonRefs.current[idx]?.focus()
    movedByKeyRef.current = false
  }, [value, buttons])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, current: number) => {
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(max, current + 1)
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = Math.max(min, current - 1)
    else if (e.key === 'Home') next = min
    else if (e.key === 'End') next = max
    if (next === null) return
    e.preventDefault()
    movedByKeyRef.current = true
    onChange(next)
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('activities.ratingAriaLabel')}
      className="flex flex-wrap items-center gap-2"
    >
      {buttons.map((n, i) => {
        const checked = value === n
        return (
          <button
            key={n}
            ref={(el) => {
              buttonRefs.current[i] = el
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            // Roving tabindex: only the selected (or first if unset) radio
            // is in the tab order. The rest are reachable via arrow keys.
            tabIndex={checked || (value == null && n === min) ? 0 : -1}
            onClick={() => onChange(n)}
            onKeyDown={(e) => handleKeyDown(e, value ?? min)}
            className={cn(
              'w-10 h-10 rounded-full border text-sm font-medium transition-colors',
              checked
                ? 'bg-[var(--theme-interactive-color)] border-[var(--theme-interactive-color)] text-white'
                : 'border-[var(--window-border-color)] text-[var(--text-color)] hover:bg-[var(--menu-item-hover)]',
            )}
          >
            {n}
          </button>
        )
      })}
    </div>
  )
}

// ============================================================================
// After-submit confirmation
// ============================================================================

function ResponderResult({
  activity,
  submission,
  onDone,
}: {
  activity: Activity
  submission: ActivitySubmission
  onDone?: () => void
}) {
  const { t } = useTranslation()
  const isAssessment = activity.analysis_mode === 'assessment'

  return (
    <div className="max-w-2xl mx-auto py-12 space-y-6 text-center">
      <div className="flex flex-col items-center gap-3">
        {isAssessment ? (
          submission.passed ? (
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          ) : (
            <XCircle className="h-12 w-12 text-red-500" />
          )
        ) : (
          <CheckCircle2 className="h-12 w-12 text-green-500" />
        )}
        <h2 className="text-xl font-semibold text-[var(--text-color)]">
          {isAssessment
            ? submission.passed
              ? t('activities.passedTitle')
              : t('activities.failedTitle')
            : t('activities.submittedTitle')}
        </h2>
        {isAssessment && submission.total_score != null && submission.max_score != null && (
          <div className="text-3xl font-bold text-[var(--text-color)]">
            {submission.total_score} / {submission.max_score}
            <div className="text-sm font-normal text-[var(--text-secondary)] mt-1">
              {Math.round((submission.total_score / submission.max_score) * 100)}%
            </div>
          </div>
        )}
        {!isAssessment && (
          <p className="text-sm text-[var(--text-secondary)]">
            {t('activities.thankYouMessage')}
          </p>
        )}
      </div>
      {activity.passing_score != null && isAssessment && (
        <div className="text-xs text-[var(--text-secondary)] flex items-center justify-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {t('activities.passingScoreHint', { score: activity.passing_score })}
        </div>
      )}
      {onDone && (
        <Button onClick={onDone} className="mt-4">
          {t('activities.done')}
        </Button>
      )}
    </div>
  )
}
